import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import { TOOL_CALL_COORDINATE_SPACE, type ToolCallBox } from "@demo/server/api/contracts/toolCall";

export interface ToolCallInputImageProps {
  /** The run's own image the tool was given, resolved by `asset_idx`; absent when it cannot be shown. */
  src?: string;
  alt: string;
  /** The part of the image the tool was given, on the 0–1000 grid; drawn as an outline over the preview. */
  region?: ToolCallBox;
  testId?: string;
}

/**
 * The preview's height: 40% of the response box, between 200px and 500px. `cqh` resolves against
 * the nearest size container, which `AgentOutput` declares on its scrolling box.
 */
const PREVIEW_HEIGHT = "[height:clamp(200px,40cqh,500px)]";

const toPercent = (coordinate: number): string => `${(coordinate / TOOL_CALL_COORDINATE_SPACE) * 100}%`;

/**
 * The Input panel of a card whose tool took one of the run's images: the image at preview height on a
 * dark gradient, with the region the tool was given outlined over it, or a picture glyph when the image
 * cannot be resolved.
 */
function ToolCallInputImage({ src, alt, region, testId }: ToolCallInputImageProps) {
  return (
    <div
      className={cn(
        PREVIEW_HEIGHT,
        "flex w-full items-center justify-center overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-b from-[#242424] to-[#181818]",
      )}
      data-testid={testId}
    >
      {src ? (
        // The image keeps its own aspect ratio inside the panel (both sides are `max-`), so its box is exactly
        // the painted image and the outline positioned against it lands on the right pixels.
        <span className="relative inline-flex max-h-full max-w-full">
          {/* The run's own media, already in the browser; next/image would need it allow-listed. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img alt={alt} className="h-auto max-h-full w-auto max-w-full" src={src} />
          {region && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute rounded-sm border-2 border-foreground-primary"
              data-testid="tool-call-input-region"
              style={{
                left: toPercent(region.top_left.x),
                top: toPercent(region.top_left.y),
                width: toPercent(region.bottom_right.x - region.top_left.x),
                height: toPercent(region.bottom_right.y - region.top_left.y),
              }}
            />
          )}
        </span>
      ) : (
        <Icon className="size-6 text-foreground-disabled" name="image" />
      )}
    </div>
  );
}

export default ToolCallInputImage;
